import api from '@/lib/axios';
import { API_ENDPOINTS } from '@/constants/api-endpoints';

export interface ReportsQuery {
  period?: string;
  startDate?: string;
  endDate?: string;
  state?: string;
}

export interface BookingReportSummary {
  totalBookings: number;
  completed: number;
  cancelled: number;
  completionRate: number;
  avgPerDay: number;
}

export interface RevenueReportSummary {
  totalRevenue: number;
  platformFee: number;
  driverPayouts: number;
  avgOrderValue: number;
  totalPayments: number;
}

export interface ChartDataPoint {
  date: string;
  value: number;
  [key: string]: string | number;
}

export interface TopDriver {
  id: string;
  name: string;
  trips: number;
  riderRevenue: number;
  platformRevenue: number;
  rating: number;
}

export interface CustomerReportData {
  acquisition: {
    newCustomers: number;
    returningCustomers: number;
    retentionRate: number;
    churnRate: number;
  };
  satisfaction: {
    averageRating: number;
    fiveStarReviews: number;
    supportTickets: number;
  };
}

export const reportsApi = {
  getStates: async (): Promise<string[]> => {
    const res = await api.get(API_ENDPOINTS.REPORTS.STATES);
    const data = (res.data as any)?.data ?? res.data;
    return data?.states ?? [];
  },

  getBookingReport: async (query: ReportsQuery): Promise<{ chartData: ChartDataPoint[]; summary: BookingReportSummary }> => {
    const res = await api.get(API_ENDPOINTS.REPORTS.BOOKINGS, { params: query });
    return (res.data as any)?.data ?? res.data;
  },

  getRevenueReport: async (query: ReportsQuery): Promise<{ chartData: ChartDataPoint[]; summary: RevenueReportSummary }> => {
    const res = await api.get(API_ENDPOINTS.REPORTS.REVENUE, { params: query });
    return (res.data as any)?.data ?? res.data;
  },

  getDriverReport: async (query: ReportsQuery): Promise<{ topDrivers: TopDriver[] }> => {
    const res = await api.get(API_ENDPOINTS.REPORTS.DRIVERS, { params: query });
    return (res.data as any)?.data ?? res.data;
  },

  getCustomerReport: async (query: ReportsQuery): Promise<CustomerReportData> => {
    const res = await api.get(API_ENDPOINTS.REPORTS.CUSTOMERS, { params: query });
    return (res.data as any)?.data ?? res.data;
  },

  exportReport: async (query: ReportsQuery & { type: string; format: 'csv' | 'pdf' }): Promise<Blob> => {
    const res = await api.get(API_ENDPOINTS.REPORTS.EXPORT, {
      params: query,
      responseType: 'blob',
    });
    return res.data;
  },
};
